/**
 * Recovery for the one failure mode a hashed-asset deploy guarantees:
 * a tab that has been open across a deploy still holds the OLD JS shell, whose
 * manifest names chunk hashes the server has just deleted. Every dynamic import
 * that tab attempts from then on 404s.
 *
 * nginx serves index.html `no-cache` and the hashed assets `immutable, 1y`
 * (docker/nginx/nginx-prod.conf), so a fresh load is always correct — only
 * already-open tabs break, and for them a reload is the whole fix.
 */

import { warn } from './console';

const LAST_CHUNK_AUTORELOAD_AT_KEY = 'rs_last_chunk_autoreload_at';
const CHUNK_AUTORELOAD_COOLDOWN_MS = 60 * 60 * 1000;

function parseStorageTimestamp(value: string | null): number | null {
  if (value == null || value === '') return null;
  const n = Number.parseInt(value, 10);
  return Number.isFinite(n) ? n : null;
}

/**
 * Dynamic import failed because the hashed chunk is missing — typical when a tab
 * kept an old JS shell after a new deploy replaced assets on the CDN.
 *
 * The message differs per engine, so all four spellings are matched: Chrome,
 * Firefox, Safari, and the `ChunkLoadError` name older bundlers set.
 */
export function isStaleBuildChunkLoadError(err: unknown): boolean {
  if (err == null) return false;
  const e = err as Partial<Error> & { name?: string; message?: string };
  const name = typeof e.name === 'string' ? e.name : '';
  const message =
    typeof e.message === 'string' ? e.message : typeof err === 'string' ? err : String(err);
  return (
    name === 'ChunkLoadError' ||
    message.includes('Failed to fetch dynamically imported module') || // Chrome / Edge
    message.includes('error loading dynamically imported module') || // Firefox
    message.includes('Importing a module script failed') || // Safari
    message.includes('Loading chunk ') // legacy bundler wording
  );
}

/**
 * Schedules a full reload once per cooldown window so a mismatched deploy can recover
 * without looping if chunks stay broken.
 */
export function tryScheduleReloadForStaleChunk(): boolean {
  try {
    const now = Date.now();
    const last = parseStorageTimestamp(sessionStorage.getItem(LAST_CHUNK_AUTORELOAD_AT_KEY));
    if (last != null && now - last < CHUNK_AUTORELOAD_COOLDOWN_MS) {
      return false;
    }
    sessionStorage.setItem(LAST_CHUNK_AUTORELOAD_AT_KEY, String(now));
  } catch {
    return false;
  }
  window.location.reload();
  return true;
}

/**
 * Reloads if `err` is a stale-build chunk failure. Returns true when the reload was
 * actually scheduled, so the caller can decide whether to swallow the error or let it
 * surface: on a cooldown hit we deliberately let it through, because a chunk that is
 * still missing an hour later is no longer a deploy race and should stay visible.
 */
export function recoverFromStaleBuildChunk(err: unknown, source: string): boolean {
  if (!isStaleBuildChunkLoadError(err)) return false;
  if (tryScheduleReloadForStaleChunk()) {
    warn(
      `[ChunkRecovery] ${source}: a JS chunk this tab was built against is no longer on the server ` +
        `(normal right after a deploy). Reloading to pick up the current build.`,
      err
    );
    return true;
  }
  warn(
    `[ChunkRecovery] ${source}: chunk still missing, and this tab already auto-reloaded within the ` +
      `last hour. Not reloading again — refresh manually. Letting the error surface.`,
    err
  );
  return false;
}

let recoveryInstalled = false;

/**
 * Installs the process-wide net for stale-chunk failures. Call once, as early in
 * startup as possible.
 *
 * Without this, the ~44 `lazy()` route imports in App.tsx have no catch anywhere:
 * the rejection is simply uncaught, and clicking a nav item does nothing at all
 * with only an `Uncaught (in promise) TypeError` in the console to show for it.
 */
export function installStaleBuildChunkRecovery(): void {
  if (recoveryInstalled || typeof window === 'undefined') return;
  recoveryInstalled = true;

  // Vite's own signal from its preload helper. It fires in production builds before
  // the rejection surfaces and carries the original error as `payload`; calling
  // preventDefault() tells Vite we have handled it and stops the rethrow.
  window.addEventListener('vite:preloadError', (event) => {
    if (recoverFromStaleBuildChunk(event.payload, 'vite:preloadError')) {
      event.preventDefault();
    }
  });

  // The catch-all for every other dynamic import: route-level lazy(), and the
  // `await import(...)` calls scattered through the stores and components.
  window.addEventListener('unhandledrejection', (event) => {
    if (recoverFromStaleBuildChunk(event.reason, 'unhandledrejection')) {
      event.preventDefault();
    }
  });
}
